import { useId } from 'react'
import {
  Field,
  FieldHint,
  FieldLabel,
  MultiCombobox,
  Select,
  SelectContent,
  SelectDescription,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  Textarea,
  type MultiComboboxOption,
} from '@firstbite/ui'
import { useListProductsCatalog } from 'services/products'

import type { SettingsField as SettingsFieldSchema } from './agentTypeMetadata'

interface Props {
  field: SettingsFieldSchema
  value: unknown
  onChange: (value: unknown) => void
  campaignProductId?: number | null
}

export function SettingsField({
  field,
  value,
  onChange,
  campaignProductId,
}: Props) {
  if (field.type === 'multiselect_products') {
    return (
      <ProductMultiSelect
        field={field}
        value={(value as number[]) ?? []}
        onChange={onChange}
        campaignProductId={campaignProductId}
      />
    )
  }
  if (field.type === 'select') {
    return <SelectField field={field} value={value} onChange={onChange} />
  }
  return <TextareaField field={field} value={value} onChange={onChange} />
}

function TextareaField({
  field,
  value,
  onChange,
}: {
  field: SettingsFieldSchema
  value: unknown
  onChange: (value: unknown) => void
}) {
  const id = useId()
  return (
    <Field>
      <FieldLabel htmlFor={id} optional={!field.required}>
        {field.label}
      </FieldLabel>
      <Textarea
        id={id}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={field.placeholder ?? undefined}
      />
      {field.helper && <FieldHint>{field.helper}</FieldHint>}
    </Field>
  )
}

function ProductMultiSelect({
  field,
  value,
  onChange,
  campaignProductId,
}: {
  field: SettingsFieldSchema
  value: number[]
  onChange: (value: number[]) => void
  campaignProductId?: number | null
}) {
  const { data } = useListProductsCatalog({
    page: 1,
    page_size: 1000,
    sort: 'name',
  })
  const selectedSet = new Set(value.map(String))
  const options: MultiComboboxOption[] = (data?.results ?? [])
    .map((p) => ({
      value: String(p.id),
      label: p.name ?? `Product #${p.id}`,
    }))
    // Selected first, then the campaign default, then alphabetical — matches the
    // old dropdown so re-opening to adjust an existing selection keeps the
    // current picks pinned to the top (not just visible as chips).
    .sort((a, b) => {
      const aSelected = selectedSet.has(a.value)
      const bSelected = selectedSet.has(b.value)
      if (aSelected !== bSelected) return aSelected ? -1 : 1
      const aDefault = a.value === String(campaignProductId)
      const bDefault = b.value === String(campaignProductId)
      if (aDefault !== bDefault) return aDefault ? -1 : 1
      return a.label.localeCompare(b.label)
    })
  const hasError = Boolean(field.required) && value.length === 0

  return (
    <div className="flex w-full flex-col gap-1.5">
      <FieldLabel optional={!field.required}>{field.label}</FieldLabel>
      <MultiCombobox
        aria-label={field.label}
        options={options}
        value={value.map(String)}
        onChange={(vals) => onChange(vals.map(Number))}
        placeholder="Select products"
        status={hasError ? 'error' : 'default'}
      />
      {field.helper && <FieldHint>{field.helper}</FieldHint>}
      {hasError && (
        <FieldHint status="error">
          Select at least one product for this agent.
        </FieldHint>
      )}
    </div>
  )
}

function SelectField({
  field,
  value,
  onChange,
}: {
  field: SettingsFieldSchema
  value: unknown
  onChange: (value: unknown) => void
}) {
  const options = field.options ?? []
  return (
    <Select
      className="w-full"
      selectedKey={value == null ? null : String(value)}
      onSelectionChange={(key) => {
        const opt = options.find((o) => String(o.value) === String(key))
        onChange(opt ? opt.value : undefined)
      }}
      placeholder="Select…"
    >
      <SelectLabel optional={!field.required}>{field.label}</SelectLabel>
      <SelectTrigger />
      {field.helper && <SelectDescription>{field.helper}</SelectDescription>}
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={String(opt.value)} id={String(opt.value)}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
